import { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShoppingCart, RefreshCw, Target, TrendingUp, Package } from "lucide-react";

export default function StylistProductsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/portal").then(res => {
      setData(res.data?.product_goals || {});
    }).catch(() => toast.error("Failed to load data")).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;
  }

  const g = data || {};
  const progressPct = g.progress_pct || 0;

  return (
    <div data-testid="stylist-products-page" className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-6">
      <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Product Sales Goals</h1>

      {/* Goal Progress */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Outfit'] font-semibold text-slate-900">Monthly Product Goal</h2>
          <span className="text-sm font-medium text-slate-600">${g.month_product_revenue || 0} / ${g.monthly_goal || 500}</span>
        </div>

        {/* Progress bar */}
        <div className="h-4 bg-slate-50 rounded-full overflow-hidden mb-3">
          <div
            data-testid="product-progress-bar"
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(progressPct, 100)}%`,
              background: progressPct >= 100 ? "#2D5936" : progressPct >= 60 ? "#F59E0B" : "#8A8A8A"
            }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{Math.round(progressPct)}% of goal</span>
          <span>{progressPct >= 100 ? "Goal reached!" : `$${Math.round((g.monthly_goal || 500) - (g.month_product_revenue || 0))} to go`}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div data-testid="stat-product-rev" className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
          <DollarIcon className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-emerald-600">${g.month_product_revenue || 0}</p>
          <p className="text-xs text-slate-400 mt-0.5">Product Revenue</p>
        </div>
        <div data-testid="stat-units-sold" className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
          <Package className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{g.product_units_sold || 0}</p>
          <p className="text-xs text-slate-400 mt-0.5">Units Sold</p>
        </div>
        <div data-testid="stat-attach-rate" className="bg-white border border-slate-200 rounded-2xl p-5 text-center">
          <Target className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{g.attachment_rate || 0}%</p>
          <p className="text-xs text-slate-400 mt-0.5">Attachment Rate</p>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-[#FAFAFA] rounded-2xl p-4 text-xs text-slate-400 space-y-1">
        <p className="font-medium text-slate-600">Boost Your Product Sales</p>
        <p>Attachment rate measures how often you sell retail with a service appointment. Top performers hit 30-40%.</p>
        <p>Recommend a take-home product that extends the client's salon results — shampoos, styling creams, and treatments are easy wins.</p>
      </div>
    </div>
  );
}

function DollarIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  );
}
